import React, { useState, useEffect, useContext, useCallback } from "react";
import { useMutation, useQuery } from "@apollo/react-hooks";

import { Editor } from "@tinymce/tinymce-react";
import PropTypes from "prop-types";

import { Card, FormLayout, Layout, TextField, PageActions, Checkbox, TextContainer, Spinner, Banner } from "@shopify/polaris";

import { PrivateContext, OnboardingContext } from "lib/context";
import constant from "lib/constant/constant";
import { baseHelper, errorHelper } from "lib/helpers";
import { SkeletonAnnotated } from "lib/components";

import { GET_EMAIL_TEMPLATE } from "app/vendors/apollo/queries";
import { BULK_INVITATION_BY_DATA } from "app/vendors/apollo/mutations";
import validate from "app/vendors/modules/generic/feature/add/subFeatures/form/yup";

const { gql } = constant;
const EMAIL_DELIMITER_REGEX = /[,\s]+/;
const EMAIL_TRIGGER_KEYS = ["Enter", "Tab", ",", " "];
const DEFAULT_INVALID_EMAIL_MESSAGE = "Please enter a valid email address.";

const Invite = (props) => {
  const {
    // learnMore
    setBanner,
  } = props;
  const { history, cms = {}, currentUser = {} } = useContext(PrivateContext) || {};
  const { isOnboarding = false, onNext, onPrevious } = useContext(OnboardingContext);

  const [emails, setEmails] = useState("");
  const [emailChips, setEmailChips] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [errorMessage, setErrorMessage] = useState({});
  const [readOnly, setCheckedValue] = useState(false);
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [submitButtonLoading, setSubmitButtonLoading] = useState(false);
  const handleChange = (value) => setCheckedValue(value);
  const [submitButton, setSubmitButton] = useState({ isDisabled: true });
  const [bulkInvitation] = useMutation(BULK_INVITATION_BY_DATA);

  const { data, loading } = useQuery(GET_EMAIL_TEMPLATE);
  const { style = {} } = currentUser || {};
  const { button = {} } = style || {};

  const getInvalidEmailMessage = useCallback(
    () => cms("section.invite.message.error.invalidEmail") || DEFAULT_INVALID_EMAIL_MESSAGE,
    [cms]
  );

  const isEmailValidationError = (error = "") => /invalid value provided in email/i.test(error);

  useEffect(() => {
    if (!data) {
      return;
    }
    const resError = baseHelper.getResponseError(data, gql.GET_BULK_EMAIL_TEMPLATE);
    if (resError) {
      setBanner({ isOpen: true, title: resError, status: constant.CRITICAL });
      return;
    }
    const resData = baseHelper.getResponseData(data, gql.GET_BULK_EMAIL_TEMPLATE);
    setMessage(resData.message);
    setSubject(resData.subject);
    setIsLoading(false);
  }, [data, setBanner]);

  const updateErrorState = (field, error) => {
    setErrorMessage((prevState) => ({
      ...prevState,
      [field]: error,
    }));
  };

  const isEmailBlank = ({ field, value, onBlurEmail = false }) => {
    if (field !== constant.EMAIL) {
      return false;
    }

    if (!value.length || (value.length === 1 && value[0] === "")) {
      updateErrorState(field, (onBlurEmail && cms("common.message.error.emailIsRequired")) || "");
      return true;
    }

    return false;
  };

  const validateEmailChip = useCallback(
    async (email) => {
      if (!email) {
        return false;
      }
      const validationError = await validate(constant.EMAIL, email, cms);
      return validationError === "";
    },
    [cms]
  );

  const syncEmailsFromChips = useCallback((chips) => {
    setEmails(chips.map((chip) => chip.value).join(","));
  }, []);

  const syncErrorFromChips = useCallback(
    (chips, isBlurEvent = false) => {
      if (!chips.length) {
        updateErrorState(constant.EMAIL, isBlurEvent ? cms("common.message.error.emailIsRequired") : "");
        return;
      }
      const hasInvalidChip = chips.some((chip) => !chip.isValid);
      updateErrorState(constant.EMAIL, hasInvalidChip ? getInvalidEmailMessage() : "");
    },
    [cms, getInvalidEmailMessage]
  );

  const addEmailChips = useCallback(
    async (value = "", isBlurEvent = false) => {
      const newEmails = value
        .split(EMAIL_DELIMITER_REGEX)
        .map((item) => item.trim())
        .filter(Boolean);
      if (!newEmails.length) {
        syncErrorFromChips(emailChips, isBlurEvent);
        return;
      }
      const validatedChips = await Promise.all(
        newEmails.map(async (email) => ({
          value: email,
          isValid: await validateEmailChip(email),
        }))
      );
      setEmailChips((prevState) => {
        const nextChips = [...prevState, ...validatedChips];
        syncEmailsFromChips(nextChips);
        syncErrorFromChips(nextChips, isBlurEvent);
        return nextChips;
      });
    },
    [emailChips, syncEmailsFromChips, syncErrorFromChips, validateEmailChip]
  );

  const removeEmailChip = (index) => {
    setEmailChips((prevState) => {
      const nextChips = prevState.filter((_, chipIndex) => chipIndex !== index);
      syncEmailsFromChips(nextChips);
      syncErrorFromChips(nextChips, true);
      return nextChips;
    });
  };

  const handleEmailInputKeyDown = async (event) => {
    if (!EMAIL_TRIGGER_KEYS.includes(event.key)) {
      return;
    }
    event.preventDefault();
    await addEmailChips(emailInput);
    setEmailInput("");
  };

  const handleEmailInputBlur = async () => {
    await addEmailChips(emailInput, true);
    setEmailInput("");
    if (!emailInput.trim()) {
      syncErrorFromChips(emailChips, true);
    }
  };

  const handleValidation = useCallback(async ({ field, valueUnderValidationChecks, onBlurEmail = false }) => {
    let value = valueUnderValidationChecks;
    if (!Array.isArray(value)) {
      value = [value];
    }

    const blankEmail = isEmailBlank({ field, value, onBlurEmail });

    if (blankEmail) {
      return;
    }

    let error = "";
    let hasError = false;
    await value.forEach(async (val, index) => {
      if (val === "" && field === constant.EMAIL) {
        hasError = true;
        updateErrorState(field, getInvalidEmailMessage());
        return;
      }
      error = await validate(field, val, cms);
      if (error !== "") {
        hasError = true;
        updateErrorState(field, field === constant.EMAIL ? getInvalidEmailMessage() : error);
        return;
      }
      if (!hasError && index === value.length - 1) {
        updateErrorState(field, "");
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getInvalidEmailMessage]);

  useEffect(() => {
    handleValidation({ field: constant.MAIL_SUBJECT, valueUnderValidationChecks: subject });
  }, [handleValidation, subject]);

  const handleEditorChange = (content) => {
    setMessage(content);
  };

  useEffect(() => {
    const hasInvalidEmailChips = emailChips.some((chip) => !chip.isValid);
    const hasEmailError = hasInvalidEmailChips || Boolean(errorMessage?.[constant.EMAIL]);
    const isAnyError = () =>
      hasEmailError || Object.entries(errorMessage).some(([field, error]) => field !== constant.EMAIL && Boolean(error));

    const isAllValuesFilled = () => {
      if (!(message && emails && subject)) return false;
      return true;
    };
    setSubmitButton((prevState) => ({
      ...prevState,
      isDisabled: isAnyError() || !isAllValuesFilled(),
    }));
  }, [subject, message, emails, errorMessage, emailChips]);

  const onSubmit = async () => {
    setSubmitButtonLoading(true);
    setSubmitButton({ isDisabled: true });
    const emailArr = emails.split(",");
    const emailsArrOfObj = emailArr.map((emailValue) => ({ email: emailValue.trim() }));
    const mutationInput = {
      emails: emailsArrOfObj,
      isReadOnly: readOnly,
      message: { message, subject },
      buttonStyle: button,
    };

    try {
      const res = await bulkInvitation({
        variables: {
          input: mutationInput,
        },
        errorPolicy: "all",
      });
      const responseData = baseHelper.getResponseData(res.data, gql.BULK_INVITATION_DATA);
      let bannerValue = {
        action: {
          id: "viewInvitations",
          content: cms("section.invite.message.success.action"),
          onAction: () => history.push("/invitation"),
        },
        status: constant.SUCCESS,
        title: cms("section.invite.message.success.invite.title"),
        // children: cms("section.invite.message.success.invite.content"),
        children: "Your vendors have been notified",
      };
      if (emailsArrOfObj.length > 1) {
        bannerValue.title = cms("section.invite.message.success.bulkInvite.title");
        bannerValue.children = "Your vendors have been notified";

        // bannerValue.children = cms("section.invite.message.success.bulkInvite.content");
      }
      setSubmitButtonLoading(false);
      if (!responseData) {
        const errorResponse = baseHelper.getResponseError(res.data, gql.BULK_INVITATION_DATA);
        if (isEmailValidationError(errorResponse)) {
          updateErrorState(constant.EMAIL, getInvalidEmailMessage());
          setEmailChips((prevState) => prevState.map((chip) => ({ ...chip, isValid: false })));
          return;
        }
        bannerValue = {
          status: constant.CRITICAL,
          title: errorResponse,
        };
      }
      setBanner({
        action: bannerValue.action,
        children: bannerValue.children,
        isOpen: true,
        status: bannerValue.status,
        title: bannerValue.title,
      });
      if (isOnboarding && responseData) {
        onNext();
      }
    } catch (exception) {
      setSubmitButtonLoading(false);
      setBanner({
        isOpen: true,
        status: constant.CRITICAL,
        title: errorHelper.parse(exception),
      });
    }
  };

  const hasInvalidEmailChips = emailChips.some((chip) => !chip.isValid);
  const emailFieldError = errorMessage?.[constant.EMAIL];
  const emailFieldHasError = hasInvalidEmailChips || Boolean(emailFieldError);
  const emailErrorMessage = emailFieldHasError
    ? emailFieldError || getInvalidEmailMessage()
    : "";

  return (
    <>
      {loading || isLoading ? (
        <SkeletonAnnotated annotatedLines={2} primaryLines={18} />
      ) : (
        // title={cms("section.invite.title")} description={cms("section.invite.description")}
        <div>
          <Layout.Section>
            <Card
              title={cms("section.invite.title")}
            // actions={[
            //   {
            //     content: cms("common.label.learnMore"),
            //     onAction: () => {
            //       learnMore(cms("section.invite.title"), cms("section.invite.todo"));
            //     },
            //   },
            // ]}
            >
              <Card.Section>
                <TextContainer>
                  {/* {cms("section.invite.inviteVendor.description")} */}

                  To invite new and prospective vendors to join your marketplace, fill in the required form fields and click ‘Submit.’ Vendors will receive an email with a unique link to set their password and access their account. For inviting more than 5 vendors, use the Bulk Vendor Invite (CSV) option by uploading a CSV file with vendor details (a sample CSV is available on the same page).
                </TextContainer>
                <br />
                <FormLayout>
                  <div>
                    <p className="vendor-invite-email-label">{`${cms("common.label.email")}*`}</p>
                    <div
                      className={`vendor-invite-email-container ${emailFieldHasError ? "vendor-invite-email-container-error" : ""
                        }`}
                    >
                      {emailChips.map((chip, index) => (
                        <span
                          key={`${chip.value}-${index}`}
                          className={`vendor-invite-email-chip ${chip.isValid ? "vendor-invite-email-chip-valid" : "vendor-invite-email-chip-invalid"
                            }`}
                        >
                          {chip.value}
                          <button
                            type="button"
                            onClick={() => removeEmailChip(index)}
                            className={`vendor-invite-email-chip-remove ${chip.isValid
                              ? "vendor-invite-email-chip-remove-valid"
                              : "vendor-invite-email-chip-remove-invalid"
                              }`}
                          >
                            x
                          </button>
                        </span>
                      ))}
                      <input
                        value={emailInput}
                        onChange={(event) => setEmailInput(event.target.value)}
                        onKeyDown={handleEmailInputKeyDown}
                        onBlur={handleEmailInputBlur}
                        placeholder={cms("section.invite.placeholder.emails")}
                        className="vendor-invite-email-input"
                      />
                    </div>
                    {emailFieldHasError ? (
                      <div className="vendor-invite-email-error-wrap">
                        <span className="vendor-invite-email-error-diamond">
                          <span className="vendor-invite-email-error-diamond-text">!</span>
                        </span>
                        <span className="vendor-invite-email-error-text">{emailErrorMessage}</span>
                      </div>
                    ) : null}
                  </div>
                  <TextField
                    label={`${cms("section.invite.label.subject")}*`}
                    placeholder={cms("section.invite.placeholder.subject")}
                    key={constant.MAIL_SUBJECT}
                    id={constant.MAIL_SUBJECT}
                    value={subject}
                    onChange={(value) => setSubject(value)}
                    error={errorMessage && errorMessage.mailSubject}
                  />
                  <p>
                    <strong>{`${cms("section.invite.inviteVendor.message")}*`}</strong>
                  </p>
                  <Editor
                    id="idTiny"
                    textareaName={cms("section.invite.inviteVendor.message")}
                    value={message}
                    onEditorChange={handleEditorChange}
                    init={{
                      menubar: true,
                      plugins: [""],
                      toolbar: cms("section.invite.inviteVendor.toolbar"),
                    }}
                  />
                </FormLayout>
              </Card.Section>
            </Card>
            <Card title={cms("section.invite.vendorAccessPermission.title")}>
              <Card.Section>
                <TextContainer>
                  <p>
                    By default, vendors are granted full access to manage their own products. Check this box if you would
                    prefer to limit them to read-only access for now.
                  </p>
                </TextContainer>
                <br />
                <FormLayout>
                  <Checkbox
                    label={cms("common.label.isReadOnlyVendor")}
                    checked={readOnly}
                    onChange={handleChange}
                    id="readOnlyCheckbox"
                  />
                </FormLayout>
              </Card.Section>
            </Card>
            <PageActions
              primaryAction={{
                content: (
                  <span style={{ display: "inline-flex", alignItems: "center" }}>
                    {submitButtonLoading ? (
                      <span style={{ display: "inline-flex", marginRight: 8 }}>
                        <Spinner accessibilityLabel={cms("common.label.loading")} size="small" />
                      </span>
                    ) : null}
                    {submitButtonLoading ? "Sending invitations" : "Send invitations"}
                  </span>
                ),
                id: "submitButton",
                onAction: () => onSubmit(),
                disabled: submitButton.isDisabled || submitButtonLoading,
              }}
              secondaryActions={[
                {
                  content: cms("common.button.cancel"),
                  id: "cancelButton",
                  onAction: () => (isOnboarding ? onPrevious() : history.push("/")),
                },
              ]}
            />
          </Layout.Section>
        </div>
      )}
    </>
  );
};

Invite.propTypes = {
  setBanner: PropTypes.func.isRequired,
  // learnMore: PropTypes.func.isRequired,
};
export default Invite;
